import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddLanguageToSurveyResult,
  ISurveyTranslation,
  IUpdateSurveyTranslationRequest,
} from "../../../@types/admin/AdminSurveyTranslation";
import { surveyTranslationsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { surveyTranslationsKey } from "../../queries/admin/surveyTranslations";

/**
 * Saves ONE translation. The endpoint has no bulk form, so the page loops
 * this over its dirty rows and reports how many succeeded.
 */
export function useUpdateSurveyTranslation(surveyId: string | undefined) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ISurveyTranslation,
    Error,
    IUpdateSurveyTranslationRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await surveyTranslationsService.updateTranslation(request)),
    // Deliberately no per-row invalidation: a batch save would refetch once
    // per row and stamp the remaining unsaved edits back to server values
    // mid-flight. The page invalidates once, after the batch.
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: surveyTranslationsKey(surveyId),
    });

  return {
    updateTranslation: mutateAsync,
    isUpdatingTranslation: isPending,
    invalidateTranslations: invalidate,
  };
}

export function useAddLanguageToSurvey(surveyId: string | undefined) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IAddLanguageToSurveyResult,
    Error,
    string
  >({
    mutationFn: async (languageCode) =>
      ensureOk(
        await surveyTranslationsService.addLanguageToSurvey(surveyId!, {
          languageCode,
        })
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveyTranslationsKey(surveyId),
      });
    },
  });

  return { addLanguageToSurvey: mutateAsync, isAddingLanguage: isPending };
}
